/**
 * Pure reminder-payload builders for medication create/update mutations.
 *
 * Extracted from useCalendarData so the schedule-clear contract (#2227/#2233)
 * is testable without React Native imports: an EXPLICIT empty schedule must
 * actively disable the linked reminder, while an omitted schedule must leave
 * it untouched (the caller skips the reminder field entirely).
 */

export interface MedicationReminderPayload {
  enabled: boolean;
  frequency: "daily";
  times: string[];
  timezone?: string;
  pushToken?: string | null;
}

/**
 * Build the reminder config to attach to a medication create/update so the
 * backend actually schedules notifications. Returns undefined when there are
 * no times to remind at. The device push token is resolved by the caller
 * (null when notification permission isn't granted — no prompt, and the
 * backend still records an in-app notification). The reminder time-of-day is
 * interpreted in the device timezone server-side.
 */
export function buildMedicationReminderPayload(
  timeTaken: string[] | undefined,
  context: { pushToken: string | null; timezone: string },
): MedicationReminderPayload | undefined {
  if (!timeTaken || timeTaken.length === 0) return undefined;
  return {
    enabled: true,
    frequency: "daily",
    times: timeTaken,
    timezone: context.timezone,
    pushToken: context.pushToken,
  };
}

/**
 * Update variant: clearing all times must actively cancel the linked reminder
 * server-side — omitting the config would leave it firing at the old times.
 * Callers must only invoke this when `timeTaken` was explicitly provided;
 * an omitted (undefined-in-payload) schedule means "leave the reminder alone"
 * and the reminder field must be skipped entirely.
 */
export function buildMedicationReminderUpdatePayload(
  timeTaken: string[] | undefined,
  context: { pushToken: string | null; timezone: string },
): MedicationReminderPayload {
  return (
    buildMedicationReminderPayload(timeTaken, context) ?? {
      enabled: false,
      frequency: "daily",
      times: [],
    }
  );
}
